'use client';

import {
  Button,
  Toaster as ChakraToaster,
  createToaster,
  Flex,
  Portal,
  Spinner,
  Stack,
  Toast,
} from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';

import {
  isStaleStateFailure,
  resolveInviteFailure,
  resolveInviteFailureText,
  type InviteMutationResult,
} from '#modules/finder/features/posts/model/inviteOutcome';
import {
  acceptSessionInvite,
  declineSessionInvite,
} from '#modules/finder/features/posts/model/sessions';
import { Avatar } from './Avatar';

export const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
});

type SessionInviteMeta = { sessionId: string; postId?: string };

const isSessionInvite = (meta: unknown): meta is { sessionInvite: SessionInviteMeta } =>
  typeof meta === 'object' &&
  meta !== null &&
  'sessionInvite' in meta &&
  typeof (meta as Record<string, unknown>).sessionInvite === 'object';

const InviteActions = ({
  sessionId,
  postId,
  href,
  toastId,
}: {
  sessionId: string;
  postId?: string;
  href?: string;
  toastId: string;
}) => {
  const { t } = useLingui();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<'accept' | 'decline' | null>(null);

  const invalidate = () => {
    if (postId) {
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
      queryClient.invalidateQueries({ queryKey: ['post-sessions', postId] });
    }
    queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
    // Полоса сессии над перепиской живёт своим кешом и своим ключом: без него она
    // держала прежний состав ещё минуту после выхода из сессии.
    queryClient.invalidateQueries({ queryKey: ['chat-peer-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['sessions'] });
    queryClient.invalidateQueries({ queryKey: ['discover-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['discover-listings'] });
    queryClient.invalidateQueries({ queryKey: ['discover-favourites'] });
    queryClient.invalidateQueries({ queryKey: ['profile-user-sessions'] });
  };

  // Отказ сервера приходит полем ответа, а не исключением, и раньше его никто не читал: тост
  // просто исчезал. Человек видел «нажал — ничего не произошло», хотя приглашение осталось
  // непринятым (сессия в Discord-голосе без привязанного Discord — самый частый случай).
  const runInviteAction = async (
    action: 'accept' | 'decline',
    mutate: () => Promise<InviteMutationResult>,
    successTitle: string
  ) => {
    setPending(action);

    try {
      const failure = resolveInviteFailure(await mutate());

      invalidate();
      toaster.dismiss(toastId);
      // «Уже в составе» и «мест не осталось» — не сбой, а отставшие данные: красный тост тут
      // обвиняет сайт в том, чего он не делал. Переход на страницу сбора ниже одинаков для всех
      // исходов, и для этих двух он особенно уместен — там сразу видно, как обстоят дела.
      const stale = failure ? isStaleStateFailure(failure) : false;
      toaster.create({
        type: stale ? 'warning' : failure ? 'error' : 'success',
        duration: 5000,
        title: failure
          ? resolveInviteFailureText(failure, {
              discordRequired: t`post:session.inviteNeedsDiscord`,
              teamFull: t`post:session.teamFullInvite`,
              alreadyMember: t`post:session.alreadyMember`,
              alreadyParticipating: t({
                id: 'post:session.alreadyParticipating',
                message: 'You already have a session at this time',
              }),
              fallback: t`Something went wrong`,
            })
          : successTitle,
      });

      // Приглашение зовёт в конкретную сессию, поэтому по «Принять» человек и должен оказаться
      // на её странице: при успехе — увидеть состав, при отказе — причину и кнопку, которая её
      // снимает (привязку Discord). Отказ от приглашения никуда не ведёт: там не на что смотреть.
      if (action === 'accept' && href) {
        router.navigate({ to: href });
      }
    } catch {
      setPending(null);
      toaster.create({ type: 'error', duration: 5000, title: t`Something went wrong` });
    }
  };

  const onAccept = () =>
    runInviteAction(
      'accept',
      () => acceptSessionInvite({ sessionId }),
      t`post:session.inviteAccepted`
    );

  const onDecline = () =>
    runInviteAction(
      'decline',
      () => declineSessionInvite({ sessionId }),
      t`post:session.inviteDeclined`
    );

  // Принять и отклонить — единственные действия тоста, и на телефоне по ним промахиваются: при
  // `size="2xs"` высота кнопки около 24px при собственном правиле в 44. Пальцем это две соседние
  // мишени в узкой полосе, где цена промаха — отказ от приглашения.
  const tapTarget = {
    '@media (any-pointer: coarse)': { minHeight: '44px', paddingInline: '12px' },
  };

  // `xs`, а не `2xs`: мышью в 24-пиксельную кнопку тоже целятся, а тост живёт пять секунд, и
  // переспросить будет негде. Отступ сверху отделяет действия от фразы — раньше они липли к
  // тексту и читались как его продолжение, а не как то, что нужно нажать.
  return (
    <Flex gap={2} pt={1} flexShrink={0}>
      <Button
        size="xs"
        css={tapTarget}
        onClick={(e) => {
          e.stopPropagation();
          onAccept();
        }}
        loading={pending === 'accept'}
        disabled={pending === 'decline'}
      >
        {t`post:session.acceptInvite`}
      </Button>
      <Button
        size="xs"
        css={tapTarget}
        variant="outline"
        colorPalette="red"
        onClick={(e) => {
          e.stopPropagation();
          onDecline();
        }}
        loading={pending === 'decline'}
        disabled={pending === 'accept'}
      >
        {t`post:session.declineInvite`}
      </Button>
    </Flex>
  );
};

export const Toaster = () => {
  const router = useRouter();

  return (
    <Portal>
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
        {(toast) => {
          const href = typeof toast.meta?.href === 'string' ? toast.meta.href : undefined;
          const avatar = typeof toast.meta?.avatar === 'string' ? toast.meta.avatar : undefined;
          const avatarName =
            typeof toast.meta?.avatarName === 'string' ? toast.meta.avatarName : undefined;

          const handleClick = href
            ? () => {
                toaster.dismiss(toast.id);
                router.navigate({ to: href });
              }
            : undefined;

          const hasAvatar = avatar !== undefined || avatarName !== undefined;
          const invite = isSessionInvite(toast.meta) ? toast.meta.sessionInvite : null;

          return (
            <Toast.Root
              width={{ md: 'sm' }}
              onClick={handleClick}
              cursor={href ? 'pointer' : undefined}
            >
              {toast.type === 'loading' ? (
                <Spinner size="sm" color="blue.solid" />
              ) : hasAvatar ? (
                <Avatar src={avatar} name={avatarName} size="xs" flexShrink={0} />
              ) : (
                <Toast.Indicator />
              )}
              <Stack gap="1" flex="1" maxWidth="100%">
                {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
                {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
                {invite && toast.id && (
                  <InviteActions
                    sessionId={invite.sessionId}
                    postId={invite.postId}
                    href={href}
                    toastId={toast.id}
                  />
                )}
              </Stack>
              {toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}
              {!toast.meta?.closable ? <Toast.CloseTrigger cursor="pointer" /> : null}
            </Toast.Root>
          );
        }}
      </ChakraToaster>
    </Portal>
  );
};
